import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLeaseCompStore, usePipedriveStore, usePopupStore } from "../..";
import useBodyContainerClassName from "../../hooks/useBodyContainerClassName";
import useClosable from "../../hooks/useClosable";
import useNavigateToCompany from "../../hooks/useNavigateToCompany";
import useRecentlyViewed from "../../hooks/useRecentlyViewed";
import DetailsType from "../../interfaces/DetailsType";
import { RecentItemType } from "../../interfaces/IRecentItem";
import { ClosableOrder } from "../../store/ClosableStore";
import { leaseCompPropertyTypeToString, leaseCompSourceToString, leaseTypeToString } from "../../utils/enumUtils";
import { formatDate, formatDateTime, formatNumber, formatPrice } from "../../utils/utils";
import { showErrorToast, showSuccessToast } from "../ToastContainer";
import AttachmentList from "../AttachmentList";
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import DetailsPanelWithFlyTo from "../DetailsPanelWithFlyTo";
import { LeaseCompFavoriteCheckbox } from "../favorites/FavoriteCheckbox";
import FieldRow from "../FieldRow";
import EditIcon from "../icons/EditIcon";
import LeaseCompIcon from "../icons/LeaseCompIcon";
import OnMapSpinner from "../spinners/OnMapSpinner";

function LeaseCompDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const leaseCompStore = useLeaseCompStore();
    const pipedriveStore = usePipedriveStore();
    const popupStore = usePopupStore();
    const leaseComp = useMemo(() => computed(() => leaseCompStore.leaseComps.find(lc => lc.id.toString() === id)), [id]).get();
    const pipedriveOrganization = useMemo(() => computed(() => leaseComp && pipedriveStore.organizations.find(o => o.id === leaseComp.pipedriveCompanyId)), [leaseComp]).get();
    const navigateToCompany = useNavigateToCompany();
    const bodyContainerClassName = useBodyContainerClassName();
    useRecentlyViewed(Number(id), RecentItemType.LeaseComp);

    const close = () => navigate("/");
    useClosable(ClosableOrder.Medium, close);

    useEffect(() => {
        if (leaseComp)
            popupStore.openLeaseCompPopup(leaseComp.id);

        return () => popupStore.close();
    }, [leaseComp]);

    if (leaseCompStore.isLeaseCompsLoading.fetching)
        return <OnMapSpinner />;

    if (!leaseComp)
        return null;

    const onUploadAttachments = async (files: File[]) => {
        try {
            for (const file of files) {
                await leaseCompStore.uploadAttachment(leaseComp.id, file);
            }

            showSuccessToast();
        } catch (e) {
            showErrorToast();
        }
    };

    const onDeleteAttachment = async (key: string) => {
        try {
            await leaseCompStore.deleteAttachment(leaseComp.id, key);
            showSuccessToast();
        } catch (e) {
            showErrorToast();
        }
    };

    return (
        <DetailsPanelWithFlyTo type={DetailsType.LeaseCompDetails} coordinates={leaseComp.centerPoint} onClose={close} controls={[
            <LeaseCompFavoriteCheckbox key="favorite" id={Number(id)} className={commonStyles.clickableIcon} />,
            <EditIcon key="edit" onClick={() => navigate(`/leaseComps/${id}/edit`)} />
        ]}>
            <div className={detailsStyles.header}>
                <div className={detailsStyles.headerGroup}>
                    <LeaseCompIcon color="#25479A" />
                    <div className={detailsStyles.title}>
                        <span className={commonStyles.link} onClick={() => popupStore.openLeaseCompPopup(leaseComp.id)}>{leaseComp.address}</span>
                    </div>
                </div>
            </div>
            <div className={commonStyles.separator}></div>
            <div className={detailsStyles.body}>
                <div className={bodyContainerClassName}>
                    <div className={detailsStyles.columns}>
                        <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                            <div className={commonStyles.header}>Lease Details</div>
                            <FieldRow label="TENANT">
                                {pipedriveOrganization && (
                                    <span className={commonStyles.link} onClick={() => navigateToCompany(pipedriveOrganization.id)}>
                                        {pipedriveOrganization.name}
                                    </span>
                                )}
                            </FieldRow>
                            <FieldRow label="ANNUAL RENT">
                                {!!leaseComp.totalRent && formatPrice(leaseComp.totalRent)}
                            </FieldRow>
                            <FieldRow label="MONTHLY RENT">
                                {!!leaseComp.totalRent && formatPrice(leaseComp.totalRent / 12)}
                            </FieldRow>
                            <FieldRow label="DATE SIGNED">
                                {!!leaseComp.signedDate && formatDate(leaseComp.signedDate)}
                            </FieldRow>
                            <FieldRow label="LEASE EXPIRATION">
                                {!!leaseComp.leaseExpiration && formatDate(leaseComp.leaseExpiration)}
                            </FieldRow>
                            <FieldRow label="LAND $/ACRE">
                                {!!leaseComp.landPsf && formatPrice(leaseComp.landPsf)}
                            </FieldRow>
                            <FieldRow label="BUILDING $/PSF">
                                {!!leaseComp.buildingPsf && formatPrice(leaseComp.buildingPsf)}
                            </FieldRow>
                        </div>
                        <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                            <div className={commonStyles.header}>Other Terms</div>
                            <FieldRow label="TYPE">
                                {!!leaseComp.type && leaseTypeToString(leaseComp.type)}
                            </FieldRow>
                            <FieldRow label="YEAR">
                                {leaseComp.year}
                            </FieldRow>
                            <FieldRow label="TI ALLOWANCE">
                                {!!leaseComp.ti && formatPrice(leaseComp.ti)}
                            </FieldRow>
                            <FieldRow label="ESCALATIONS">
                                {leaseComp.escalations}
                            </FieldRow>
                            <FieldRow label="OPTIONS">
                                {leaseComp.options}
                            </FieldRow>
                        </div>
                    </div>
                    <div className={detailsStyles.columns}>
                        <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                            <div className={commonStyles.header}>Property Details</div>
                            <FieldRow label="TENANT LAND SIZE">
                                {!!leaseComp.tenantLandSize && `${formatNumber(leaseComp.tenantLandSize)} Acres`}
                            </FieldRow>
                            <FieldRow label="PARCEL SIZE">
                                {!!leaseComp.parcelSizeAcres && `${formatNumber(leaseComp.parcelSizeAcres)} Acres`}
                            </FieldRow>
                            <FieldRow label="BUILDING SIZE">
                                {!!leaseComp.buildingSize && `${formatNumber(leaseComp.buildingSize)} Sq-Ft`}
                            </FieldRow>
                            <FieldRow label="ZONING">
                                {leaseComp.zoning}
                            </FieldRow>
                            <FieldRow label="PROPERTY TYPE">
                                {!!leaseComp.propertyType && leaseCompPropertyTypeToString(leaseComp.propertyType)}
                            </FieldRow>
                        </div>
                        <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                            <div className={commonStyles.header}>Internal</div>
                            <FieldRow label="COMP SOURCE">
                                {!!leaseComp.compSource && leaseCompSourceToString(leaseComp.compSource)}
                            </FieldRow>
                            <FieldRow label="NOTES">
                                {leaseComp.notes}
                            </FieldRow>
                            <FieldRow label="LAST MODIFIED">
                                <span>{leaseComp.modifiedByUser}</span>
                                <br />
                                <span>{formatDateTime(leaseComp.modifiedDate)}</span>
                            </FieldRow>
                        </div>
                    </div>
                    <div className={detailsStyles.columns}>
                        <div className={detailsStyles.column}>
                            <div className={commonStyles.header}>Attachments</div>
                            <AttachmentList attachments={leaseComp.attachments} onUpload={onUploadAttachments} onDelete={onDeleteAttachment} />
                        </div>
                    </div>
                </div>
            </div>
        </DetailsPanelWithFlyTo>
    );
}

export default observer(LeaseCompDetails);
